// sapi-admin/src/components/DeliveryDashboard.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Truck,
  Package, ChevronRight,
  RefreshCw, Loader2, X,
  Bell
} from 'lucide-react';
import { getDeliveryOrders, sendDeliveryReminder } from '../services/deliveryTrackingService';
import { toast } from 'react-toastify';
import FormatPrice from '../components/FormatPrice';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase/config';

const GlassPanel = ({ children }) => (
  <div className="bg-[#111827]/80 backdrop-blur-xl rounded-2xl border border-white/[0.06] shadow-xl overflow-hidden">
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    'Shipped': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'Approved': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'Packed': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    'Delivered': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Delayed': 'bg-red-500/20 text-red-400 border-red-500/30'
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${colors[status] || colors['Shipped']}`}>
      {status}
    </span>
  );
};

const UrgencyBadge = ({ urgency }) => {
  const colors = {
    '✅ On Time': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    '⚠️ Approaching': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    '🔴 Urgent': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    '🚨 OVERDUE': 'bg-red-500/20 text-red-400 border-red-500/30'
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${colors[urgency] || colors['✅ On Time']}`}>
      {urgency}
    </span>
  );
};

export default function DeliveryDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    urgent: 0,
    onTime: 0,
    overdue: 0
  });

  const loadOrders = async () => {
  setLoading(true);
  try {
    const data = await getDeliveryOrders();
    console.log('📦 Orders loaded:', data); // ← ADD THIS
    setOrders(data);
    
    setStats({
      total: data.length,
      urgent: data.filter(o => o.urgency.priority >= 2).length,
      onTime: data.filter(o => o.urgency.priority === 0).length,
      overdue: data.filter(o => o.urgency.priority === 3).length
    });
  } catch (error) {
    console.error('Error loading orders:', error);
    toast.error('Failed to load delivery orders');
  } finally {
    setLoading(false);
  }
};

  // In DeliveryDashboard.jsx - add this useEffect temporarily

useEffect(() => {
  const debugOrders = async () => {
    try {
      const ordersRef = collection(db, 'orders');
      const snapshot = await getDocs(query(ordersRef, orderBy('orderDate', 'desc')));
      const orders = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        orders.push({
          id: doc.id,
          status: data.status,
          orderId: data.orderId,
          orderDate: data.orderDate,
          shippingMethod: data.shipping?.method,
          estimatedDays: data.shipping?.estimatedDelivery || data.shipping?.deliveryDays
        });
      });
      console.table(orders);
    } catch (error) {
      console.error('Debug error:', error);
    }
  };
  
  debugOrders();
}, []);

  useEffect(() => {
    loadOrders();
    // Refresh every 5 minutes
    const interval = setInterval(loadOrders, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSendReminder = async (order) => {
    setSendingReminder(order.id);
    try {
        // ✅ Retrieve admin phone number from Firestore settings
        const settingsRef = doc(db, 'settings', 'admin');
        const settingsSnap = await getDoc(settingsRef);
        const adminPhone = settingsSnap.exists() 
        ? settingsSnap.data().whatsappPhone 
        : '+237677077030'; // Fallback
        
        const result = await sendDeliveryReminder(order, adminPhone);
        if (result.success) {
        toast.success(`✅ Reminder sent for #${order.orderId || order.id}`);
        } else {
        toast.error(`❌ Error: ${result.error}`);
        }
    } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to send reminder');
    } finally {
        setSendingReminder(false);
    }
    };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'urgent') return order.urgency.priority >= 2;
    if (filter === 'overdue') return order.urgency.priority === 3;
    if (filter === 'ontime') return order.urgency.priority === 0;
    return true;
  });

  if (loading && orders.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <GlassPanel>
          <div className="p-4 text-center">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-xs text-slate-400">Total to deliver</p>
          </div>
        </GlassPanel>
        <GlassPanel>
          <div className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{stats.onTime}</p>
            <p className="text-xs text-slate-400">✅ On Time</p>
          </div>
        </GlassPanel>
        <GlassPanel>
          <div className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-400">{stats.urgent}</p>
            <p className="text-xs text-slate-400">⚠️ Urgent</p>
          </div>
        </GlassPanel>
        <GlassPanel>
          <div className="p-4 text-center">
            <p className="text-2xl font-bold text-red-400">{stats.overdue}</p>
            <p className="text-xs text-slate-400">🚨 Overdue</p>
          </div>
        </GlassPanel>
      </div>

      {/* Filters and refresh */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              filter === 'all' 
                ? 'bg-purple-600 text-white' 
                : 'bg-white/5 text-slate-400 hover:bg-white/10'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('urgent')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              filter === 'urgent' 
                ? 'bg-orange-600 text-white' 
                : 'bg-white/5 text-slate-400 hover:bg-white/10'
            }`}
          >
            ⚠️ Urgent
          </button>
          <button
            onClick={() => setFilter('overdue')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              filter === 'overdue' 
                ? 'bg-red-600 text-white' 
                : 'bg-white/5 text-slate-400 hover:bg-white/10'
            }`}
          >
            🚨 Overdue
          </button>
          <button
            onClick={() => setFilter('ontime')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              filter === 'ontime' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-white/5 text-slate-400 hover:bg-white/10'
            }`}
          >
            ✅ On Time
          </button>
        </div>
        <button
          onClick={loadOrders}
          disabled={loading}
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 transition"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Order list */}
      <GlassPanel>
        <div className="divide-y divide-white/[0.04]">
          {filteredOrders.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Truck size={32} className="mx-auto mb-3 opacity-30" />
              <p>No orders to deliver</p>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 hover:bg-white/5 transition cursor-pointer ${
                  order.urgency.priority >= 2 ? 'bg-red-500/5' : ''
                }`}
                onClick={() => { setSelectedOrder(order); setShowModal(true); }}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {/* Order info */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      order.urgency.priority >= 2 ? 'bg-red-500/20' : 'bg-purple-500/20'
                    }`}>
                      <Package size={16} className={order.urgency.priority >= 2 ? 'text-red-400' : 'text-purple-400'} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-white truncate">
                          #{order.orderId || order.id?.slice(-8)?.toUpperCase()}
                        </p>
                        <StatusBadge status={order.status} />
                        <UrgencyBadge urgency={order.urgency.label} />
                      </div>
                      <p className="text-xs text-slate-400 truncate">
                        {order.userName || 'Unknown'} • {order.userPhone || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Delivery details */}
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Estimate</p>
                      <p className="text-sm font-semibold text-white">
                        {order.shipping?.estimatedDays || '7 days'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Days remaining</p>
                      <p className={`text-sm font-semibold ${
                        order.urgency.priority >= 2 ? 'text-red-400' : 'text-emerald-400'
                      }`}>
                        {order.daysRemaining > 0 ? `${order.daysRemaining}d` : '⚠️ Deadline exceeded'}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendReminder(order);
                      }}
                      disabled={sendingReminder === order.id}
                      className="p-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 transition disabled:opacity-50"
                      title="Send a reminder"
                    >
                      {sendingReminder === order.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Bell size={14} />
                      )}
                    </button>
                    <ChevronRight size={16} className="text-slate-500" />
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </GlassPanel>

      {/* Details Modal */}
      <AnimatePresence>
        {showModal && selectedOrder && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0d1526] border border-white/10 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-white/10 flex justify-between items-center">
                <h3 className="text-lg font-bold text-white">
                  Delivery Details
                </h3>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {/* Order info */}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Order</span>
                    <span className="text-white font-semibold">#{selectedOrder.orderId || selectedOrder.id?.slice(-8)?.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Customer</span>
                    <span className="text-white">{selectedOrder.userName || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Phone</span>
                    <span className="text-white">{selectedOrder.userPhone || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Status</span>
                    <StatusBadge status={selectedOrder.status} />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Urgency</span>
                    <UrgencyBadge urgency={selectedOrder.urgency.label} />
                  </div>
                </div>

                {/* Delivery details */}
                <div className="bg-white/5 rounded-xl p-4 space-y-2">
                  <p className="text-sm font-semibold text-white">📦 Delivery Details</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Method</span>
                    <span className="text-white">{selectedOrder.shipping?.method || 'Standard'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Estimate</span>
                    <span className="text-white">{selectedOrder.shipping?.estimatedDays || '7 days'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Cost</span>
                    <span className="text-white">{FormatPrice(selectedOrder.shipping?.cost || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Days remaining</span>
                    <span className={selectedOrder.daysRemaining > 0 ? 'text-emerald-400' : 'text-red-400'}>
                      {selectedOrder.daysRemaining > 0 ? `${selectedOrder.daysRemaining} days` : '⚠️ Deadline exceeded'}
                    </span>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-sm font-semibold text-white mb-2">📍 Delivery Address</p>
                  <p className="text-sm text-slate-300">
                    {selectedOrder.shipping?.address?.street || 
                     `${selectedOrder.shippingAddress?.houseNo || ''} ${selectedOrder.shippingAddress?.line1 || ''}`.trim()}
                  </p>
                  <p className="text-sm text-slate-300">
                    {selectedOrder.shippingAddress?.city || selectedOrder.shipping?.address?.city}
                  </p>
                  <p className="text-sm text-slate-300">
                    {selectedOrder.shippingAddress?.country || 'Cameroon'}
                  </p>
                </div>

                {/* Actions */}
                <button
                  onClick={() => {
                    handleSendReminder(selectedOrder);
                    setShowModal(false);
                  }}
                  disabled={sendingReminder === selectedOrder.id}
                  className="w-full py-3 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-xl font-semibold transition flex items-center justify-center gap-2"
                >
                  {sendingReminder === selectedOrder.id ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Bell size={16} />
                  )}
                  Send a reminder
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}